/**
 * Argus Live — create-new-dashboard route.
 *
 * `/argus-live/dashboards/new` — minimal form for the three required
 * fields (slug + title + optional description). On create, redirects
 * to the 3-pane editor at `/argus-live/dashboards/:id/edit` where the
 * user starts chatting.
 *
 * The form intentionally does NOT collect HTML/JS/CSS — the chat-driven
 * authoring loop produces those. We pre-fill the slug from the title
 * (kebab-cased, alphanumerics + . _ -) to match the server-side regex
 * in `CreateDashboardBody` so the user rarely has to think about it.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useHasPermission } from '@/lib/use-has-permission';
import { useCreateArgusLiveDashboard } from '@/lib/use-argus-live';
import {
  isValidArgusLiveSlug,
  titleToSlug,
} from '@/lib/argus-live-helpers';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/argus-live/dashboards/new')({
  component: ArgusLiveNewDashboardRoute,
});

function ArgusLiveNewDashboardRoute() {
  const navigate = useNavigate();
  const canWrite = useHasPermission('write', 'argus_live');
  const createMutation = useCreateArgusLiveDashboard();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  // Auto-derive slug from title until the user manually edits it.
  const derivedSlug = useMemo(() => titleToSlug(title), [title]);
  const effectiveSlug = slugTouched ? slug : derivedSlug;

  const isValid =
    title.trim().length > 0 && isValidArgusLiveSlug(effectiveSlug);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      const res = await createMutation.mutateAsync({
        slug: effectiveSlug,
        title: title.trim(),
        description: description.trim() || null,
      });
      toast.success(`Created "${res.data.title}"`);
      navigate({
        to: '/argus-live/dashboards/$id/edit',
        params: { id: res.data.id },
      });
    } catch (err) {
      const e = err as Error & { status?: number };
      if (e.status === 409) {
        toast.error('That slug is taken. Try another.');
      } else {
        toast.error(`Create failed: ${e.message}`);
      }
    }
  };

  if (!canWrite) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <Sparkles className="h-10 w-10 text-amber-500/70 mb-3" />
        <h2 className="text-base font-semibold mb-1">
          You can't create Argus Live dashboards
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mb-4">
          Creating dashboards requires the{' '}
          <code className="font-mono">write:argus_live</code> permission.
          Tenant admins can grant it via the Users module.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: '/argus-live' })}
        >
          Back to dashboards
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="border-b border-border/40 px-6 py-4 flex items-center gap-3 shrink-0 bg-background">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => navigate({ to: '/argus-live' })}
          aria-label="Back to dashboards"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-base font-semibold">New Argus Live dashboard</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Give it a name. You'll chat with Argus on the next screen to build it.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <form
          onSubmit={onSubmit}
          className="max-w-xl mx-auto"
          data-testid="argus-live-new-dashboard-form"
        >
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-xs">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Sales Pipeline"
                  maxLength={200}
                  autoFocus
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug" className="text-xs flex items-center gap-2">
                  Slug
                  <span className="text-muted-foreground font-normal">
                    (lowercase, dots / underscores / dashes only)
                  </span>
                </Label>
                <Input
                  id="slug"
                  value={effectiveSlug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setSlugTouched(true);
                  }}
                  placeholder="sales-pipeline"
                  maxLength={120}
                  className={cn(
                    'font-mono text-sm',
                    !isValid && title.length > 0 && 'border-destructive',
                  )}
                  aria-invalid={!isValid && title.length > 0}
                />
                {effectiveSlug && !isValid && title.length > 0 && (
                  <p className="text-[11px] text-destructive">
                    Slug must start with a letter or digit and only contain
                    lowercase letters, digits, dots, underscores, or dashes.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-xs">
                  Description{' '}
                  <span className="text-muted-foreground font-normal">
                    (optional)
                  </span>
                </Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What does this dashboard show?"
                  maxLength={2000}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate({ to: '/argus-live' })}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Creating…
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  Create and start chatting
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
